import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput } from "react-native";
import { Ionicons } from "@expo/vector-icons";

const ColorSwatchView = ({ onSelectColor, initialColor, selectedColor }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [activeFilter, setActiveFilter] = useState("all");

	const handleColorSelect = (color) => {
		if (onSelectColor) {
			onSelectColor(color);
		}
	};

	// Filter colors based on search query (hex code or color name)
	const filterColors = (colorArray) => {
		if (!searchQuery.trim()) return colorArray;

		return colorArray.filter((color) => color.toLowerCase().includes(searchQuery.toLowerCase()));
	};

	// Predefined color palettes
	const brandColors = ["#82C91E", "#4C6EF5", "#FF6B6B", "#FF922B", "#339AF0", "#845EF7", "#FF6B6B", "#20C997", "#F06595"];

	const textColors = ["#FFFFFF", "#F8F9FA", "#E9ECEF", "#DEE2E6", "#CED4DA", "#ADB5BD", "#9E9E9E", "#6C757D", "#495057", "#343A40", "#212529"];

	const allColors = [
		"#FF5252",
		"#FF4081",
		"#E040FB",
		"#7C4DFF",
		"#536DFE",
		"#448AFF",
		"#40C4FF",
		"#18FFFF",
		"#64FFDA",
		"#69F0AE",
		"#B2FF59",
		"#EEFF41",
		"#FFFF00",
		"#FFD740",
		"#FFAB40",
		"#FF6E40",
		"#212121",
		"#424242",
		"#616161",
		"#757575",
		"#9E9E9E",
		"#BDBDBD",
		"#E0E0E0",
		"#FFFFFF",
	];

	// Determine which color set to display based on filter
	const getColorSet = () => {
		switch (activeFilter) {
			case "brand":
				return filterColors(brandColors);
			case "text":
				return filterColors(textColors);
			case "all":
			default:
				return filterColors(allColors);
		}
	};

	return (
		<View style={styles.container}>
			<View style={styles.searchContainer}>
				<Ionicons name="color-palette" size={20} color="#666" style={styles.searchIcon} />
				<TextInput style={styles.searchInput} placeholder="Search colors by name or hex code..." placeholderTextColor="#666" value={searchQuery} onChangeText={setSearchQuery} />
			</View>

			<View style={styles.filterTabs}>
				<TouchableOpacity style={[styles.filterTab, activeFilter === "all" && styles.activeFilterTab]} onPress={() => setActiveFilter("all")}>
					<Text style={styles.filterTabText}>All</Text>
				</TouchableOpacity>
				<TouchableOpacity style={[styles.filterTab, activeFilter === "brand" && styles.activeFilterTab]} onPress={() => setActiveFilter("brand")}>
					<Text style={styles.filterTabText}>Brand</Text>
				</TouchableOpacity>
				<TouchableOpacity style={[styles.filterTab, activeFilter === "text" && styles.activeFilterTab]} onPress={() => setActiveFilter("text")}>
					<Text style={styles.filterTabText}>Text</Text>
				</TouchableOpacity>
				<TouchableOpacity style={[styles.filterTab, activeFilter === "custom" && styles.activeFilterTab]} onPress={() => setActiveFilter("custom")}>
					<Text style={styles.filterTabText}>Custom</Text>
				</TouchableOpacity>
			</View>

			<ScrollView style={styles.colorGrid} contentContainerStyle={styles.colorGridContent}>
				{/* Color Palette */}
				{getColorSet().map((color, index) => (
					<TouchableOpacity key={index} style={[styles.colorItem, { backgroundColor: color }, selectedColor === color && styles.selectedColorItem]} onPress={() => handleColorSelect(color)}>
						{selectedColor === color && <Ionicons name="checkmark" size={20} color={isLightColor(color) ? "#000" : "#fff"} />}
					</TouchableOpacity>
				))}
			</ScrollView>

			<View style={styles.colorInfo}>
				<View style={styles.selectedColorPreview}>
					<View style={[styles.colorPreview, { backgroundColor: selectedColor || initialColor || "#transparent" }]} />
					<Text style={styles.colorHex}>{selectedColor || initialColor || "No color selected"}</Text>
				</View>

				<TouchableOpacity style={styles.addColorButton}>
					<Ionicons name="add" size={20} color="#fff" />
					<Text style={styles.addColorText}>Add Custom</Text>
				</TouchableOpacity>
			</View>
		</View>
	);
};

// Helper function to determine if a color is light or dark
const isLightColor = (color) => {
	// Convert hex to RGB
	const hex = color.replace("#", "");
	const r = parseInt(hex.substr(0, 2), 16);
	const g = parseInt(hex.substr(2, 2), 16);
	const b = parseInt(hex.substr(4, 2), 16);

	// Calculate perceived brightness (YIQ formula)
	const yiq = (r * 299 + g * 587 + b * 114) / 1000;
	return yiq >= 128;
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
	},
	searchContainer: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		paddingHorizontal: 12,
		marginBottom: 16,
	},
	searchIcon: {
		marginRight: 8,
	},
	searchInput: {
		flex: 1,
		height: 40,
		color: "#fff",
		fontSize: 14,
	},
	filterTabs: {
		flexDirection: "row",
		marginBottom: 16,
		gap: 8,
	},
	filterTab: {
		paddingVertical: 6,
		paddingHorizontal: 12,
		borderRadius: 4,
		backgroundColor: "#2A2A2A",
	},
	activeFilterTab: {
		backgroundColor: "#333",
	},
	filterTabText: {
		color: "#fff",
		fontSize: 12,
	},
	colorGrid: {
		flex: 1,
	},
	colorGridContent: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 12,
		paddingBottom: 20,
	},
	colorItem: {
		width: 48,
		height: 48,
		justifyContent: "center",
		alignItems: "center",
		borderRadius: 4,
		borderWidth: 1,
		borderColor: "rgba(255, 255, 255, 0.1)",
	},
	selectedColorItem: {
		borderColor: "#fff",
		borderWidth: 2,
	},
	colorInfo: {
		marginTop: 16,
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	selectedColorPreview: {
		flexDirection: "row",
		alignItems: "center",
	},
	colorPreview: {
		width: 24,
		height: 24,
		borderRadius: 4,
		marginRight: 8,
		borderWidth: 1,
		borderColor: "rgba(255, 255, 255, 0.2)",
	},
	colorHex: {
		color: "#fff",
		fontSize: 14,
	},
	addColorButton: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#333",
		paddingHorizontal: 12,
		paddingVertical: 6,
		borderRadius: 4,
		gap: 6,
	},
	addColorText: {
		color: "#fff",
		fontSize: 12,
	},
});

export default ColorSwatchView;
